import { autoscaleConditionOperators, autoscaleConditionTypes } from '../autoscaleRulesMenuItems';
import { Condition } from '../useAutoscaleRulesBuilder';

import type { Account } from '~/shared/utils/types';

function generateId(): string {
  return Math.random().toString(36).slice(2, 8);
}

interface AutotagRule {
  organizationIdentifier: string;
  tagKey: string;
  tagValue: string;
  matchEmail?: boolean;
  matchAccountName?: boolean;
}

const AUTOTAG_RULES: AutotagRule[] = [
  {
    organizationIdentifier: 'codygo',
    tagKey: 'Name',
    tagValue: 'Codygo',
    matchEmail: true,
    matchAccountName: true,
  },
];

function getApplicableAutotags(
  userEmail: string | undefined,
  accounts: Account[] | undefined
): AutotagRule[] {
  if (!userEmail && !accounts?.length) {
    return [];
  }

  const emailLower = userEmail?.toLowerCase() || '';

  return AUTOTAG_RULES.filter((rule) => {
    const identifierLower = rule.organizationIdentifier.toLowerCase();

    const emailMatch = rule.matchEmail && emailLower.endsWith(`@${identifierLower}.com`);

    const accountMatch =
      rule.matchAccountName &&
      accounts?.some((account) => account.accountName?.toLowerCase() === identifierLower);

    return emailMatch || accountMatch;
  });
}

function createAutotagCondition(autotag: AutotagRule): Condition {
  const volumeTagType = autoscaleConditionTypes.find((t) => t.tag === 'volume');
  const isOperator = autoscaleConditionOperators.find((o) => o.operator === 'is');

  if (!volumeTagType || !isOperator) {
    throw new Error('Required autoscale condition types/operators not found');
  }

  return {
    id: `autotag-${autotag.organizationIdentifier}-${generateId()}`,
    type: volumeTagType,
    conditionOperators: [
      {
        id: `autotag-op-${autotag.organizationIdentifier}-${generateId()}`,
        condition: isOperator,
        value: [`${autotag.tagKey}:${autotag.tagValue}`],
      },
    ],
  };
}

export function injectAutotags(
  conditions: Condition[],
  userEmail: string | undefined,
  accounts: Account[] | undefined
): Condition[] {
  const applicableAutotags = getApplicableAutotags(userEmail, accounts);

  if (applicableAutotags.length === 0) {
    return conditions;
  }

  const existingVolumeTagConditions = conditions.filter((c) => c.type?.tag === 'volume');

  const autotagConditions: Condition[] = [];

  for (const autotag of applicableAutotags) {
    const autotagValue = `${autotag.tagKey}:${autotag.tagValue}`;

    const alreadyExists = existingVolumeTagConditions.some((condition) =>
      condition.conditionOperators.some((op) => op.value.includes(autotagValue))
    );

    if (!alreadyExists) {
      autotagConditions.push(createAutotagCondition(autotag));
    }
  }

  return [...autotagConditions, ...conditions];
}
